import React, { useEffect, useMemo, useState } from 'react';

import {
  BurgerButton,
  Code,
  CodeCard,
  CodeHeader,
  Container,
  CopyButton,
  Header,
  HeaderLinkButton,
  HeaderLinks,
  InlineCode,
  Layout,
  LangButton,
  LangRow,
  NavGroupTitle,
  NavSectionTitle,
  NavSubGroupTitle,
  NavChevron,
  NavItem,
  MobileTopBar,
  Page,
  SearchBox,
  SearchInput,
  Section,
  SectionTitle,
  Sidebar,
  SidebarBackdrop,
  SidebarHeader,
  SidebarTitle,
  Subtitle,
  Text,
  Title,
} from './styles';

import { getDocumentationSections } from './content';

const useCopy = () => {
  const [copiedKey, setCopiedKey] = useState(null);

  const copy = async (key, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      window.setTimeout(() => setCopiedKey(null), 900);
      return true;
    } catch (e) {
      setCopiedKey(null);
      return false;
    }
  };

  return { copiedKey, copy };
};

const groupByCategory = (sections) => {
  const map = new Map();
  (sections || []).forEach((s) => {
    const cat = s.category || 'Разное';
    if (!map.has(cat)) map.set(cat, []);
    map.get(cat).push(s);
  });
  return Array.from(map.entries());
};

const groupByNav = (sections) => {
  const byGroup = new Map();
  (sections || []).forEach((s) => {
    const group = s.navGroup || null;
    const subgroup = s.navSubgroup || null;
    if (!group) return;
    if (!byGroup.has(group)) byGroup.set(group, { group, order: s.navGroupOrder ?? 0, subgroups: new Map(), items: [] });
    const g = byGroup.get(group);
    g.order = Math.min(g.order, s.navGroupOrder ?? 0);
    if (subgroup) {
      if (!g.subgroups.has(subgroup))
        g.subgroups.set(subgroup, { subgroup, order: s.navSubgroupOrder ?? 0, items: [] });
      const sg = g.subgroups.get(subgroup);
      sg.order = Math.min(sg.order, s.navSubgroupOrder ?? 0);
      sg.items.push(s);
    } else {
      g.items.push(s);
    }
  });

  const groups = Array.from(byGroup.values()).sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.group.localeCompare(b.group));
  return groups.map((g) => ({
    ...g,
    items: (g.items || []).sort((a, b) => (a.navOrder ?? 0) - (b.navOrder ?? 0) || (a.title || '').localeCompare(b.title || '')),
    subgroups: Array.from(g.subgroups.values())
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.subgroup.localeCompare(b.subgroup))
      .map((sg) => ({
        ...sg,
        items: (sg.items || []).sort(
          (a, b) => (a.navOrder ?? 0) - (b.navOrder ?? 0) || (a.title || '').localeCompare(b.title || ''),
        ),
      })),
  }));
};

const COMPACT_LIST_TITLES = new Set([
  'Заголовки',
  'Headers',
  'Параметры пути',
  'Path params',
  'Параметры запроса',
  'Query params',
  'Коды ответов',
  'Response codes',
  'Типовые ошибки и что делать',
  'Common errors and what to do',
  'Методы записи',
  'Write endpoints',
  'Метод записи',
  'Write endpoint',
]);

const TABLE_LIST_TITLES = new Set([
  'Заголовки',
  'Headers',
  'Параметры пути',
  'Path params',
  'Параметры запроса',
  'Query params',
  'Коды ответов',
  'Response codes',
]);

const isRequestFieldsTitle = (title) => {
  const t = String(title || '').trim().toLowerCase();
  return t.includes('поля запроса') || t.includes('request fields');
};

const parseFieldSpec = (text, lang) => {
  const isRu = lang !== 'en';
  const m = String(text || '').trim().match(/^([a-zA-Z0-9_]+)\s*\(([^)]*)\)\s*$/);
  if (!m) return { field: String(text || '').trim(), type: '-', required: '-', description: '' };
  const field = m[1];
  const parts = String(m[2] || '')
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);
  const type = parts[0] || '-';
  const tail = parts.slice(1).join(', ');
  const tailLower = tail.toLowerCase();
  let required = '-';
  if (tailLower.includes('обязательно') || tailLower.includes('required')) required = isRu ? 'Да' : 'Yes';
  else if (tailLower.includes('опционально') || tailLower.includes('optional')) required = isRu ? 'Нет' : 'No';
  return { field, type, required, description: tail };
};

const parseListRow = (title, item, lang) => {
  const isRu = lang !== 'en';
  const text = String(item || '').trim();
  const titleNorm = String(title || '').trim();

  if (titleNorm === 'Заголовки' || titleNorm === 'Headers') {
    const i = text.indexOf(':');
    if (i !== -1) {
      const name = text.slice(0, i).trim();
      const val = text.slice(i + 1).trim();
      return {
        field: name,
        type: 'header',
        required: isRu ? 'Да' : 'Yes',
        description: val,
      };
    }
    return { field: text, type: 'header', required: isRu ? 'Да' : 'Yes', description: '' };
  }

  if (titleNorm === 'Параметры пути' || titleNorm === 'Path params') {
    const row = parseFieldSpec(text, lang);
    return { ...row, required: isRu ? 'Да' : 'Yes' };
  }

  if (titleNorm === 'Параметры запроса' || titleNorm === 'Query params') {
    const row = parseFieldSpec(text, lang);
    return { ...row, required: isRu ? 'Нет' : 'No' };
  }

  if (titleNorm === 'Коды ответов' || titleNorm === 'Response codes') {
    const m = text.match(/^([0-9]{3})\s*[—-]\s*(.+)$/);
    if (m) {
      return { field: m[1], type: 'http', required: '-', description: m[2] };
    }
    return { field: text, type: 'http', required: '-', description: '' };
  }

  if (isRequestFieldsTitle(titleNorm)) {
    return parseFieldSpec(text, lang);
  }

  return { field: text, type: '-', required: '-', description: '' };
};

const getHashSectionId = () => {
  if (typeof window === 'undefined') return '';
  const raw = String(window.location.hash || '').replace(/^#/, '').trim();
  if (!raw) return '';
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
};

const setHashSectionId = (id, replace = false) => {
  if (typeof window === 'undefined') return;
  const value = String(id || '').trim();
  if (!value) return;
  const nextHash = `#${encodeURIComponent(value)}`;
  if (window.location.hash === nextHash) return;
  const nextUrl = `${window.location.pathname}${window.location.search}${nextHash}`;
  if (replace) {
    window.history.replaceState(null, '', nextUrl);
  } else {
    window.history.pushState(null, '', nextUrl);
  }
};

const parseEndpointTitle = (title) => {
  const raw = String(title || '').trim();
  const m = raw.match(/^(GET|POST|PUT|DELETE|PATCH)\s+(.+)$/i);
  if (!m) return null;
  return { method: m[1].toUpperCase(), path: m[2] };
};

const parseRequestLeadText = (value) => {
  const raw = String(value || '').trim();
  const m = raw.match(/^(GET|POST|PUT|DELETE|PATCH)\s+(\/api\/[^\s]+)\s+([\s\S]+)$/i);
  if (!m) return null;
  return { method: m[1].toUpperCase(), path: m[2], description: m[3] };
};

const getBaseUrlFromBlocks = (blocks) => {
  const items = blocks || [];
  for (const b of items) {
    if (b?.type !== 'text') continue;
    const value = String(b.value || '');
    const m = value.match(/https?:\/\/[^\s`]+/i);
    if (m) return m[0];
  }
  return null;
};

const buildEndpointUrl = (baseUrl, path) => {
  const b = String(baseUrl || '').replace(/\/+$/, '');
  const p = String(path || '');
  if (b.endsWith('/api/v1') && p.startsWith('/api/v1')) {
    return `${b.slice(0, -7)}${p}`;
  }
  return `${b}${p}`;
};

const toJsFromCurl = (curlCode) => {
  const src = String(curlCode || '');
  const urlMatch = src.match(/"([^"]+)"/);
  const methodMatch = src.match(/-X\s+([A-Z]+)/i);
  const headerMatches = [...src.matchAll(/-H\s+"([^"]+)"/g)];
  const bodyMatch = src.match(/-d\s+'([\s\S]*)'$/m);
  const url = urlMatch ? urlMatch[1] : 'https://api.example.com/path';
  const method = methodMatch ? methodMatch[1].toUpperCase() : 'GET';
  const headers = headerMatches
    .map((h) => h[1])
    .filter(Boolean)
    .map((line) => {
      const i = line.indexOf(':');
      if (i === -1) return null;
      const k = line.slice(0, i).trim();
      const v = line.slice(i + 1).trim();
      return [k, v];
    })
    .filter(Boolean);
  const hasBody = Boolean(bodyMatch && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method));
  const headerLines = headers.map(([k, v]) => `    "${k}": "${v}",`).join('\n');
  const bodyRaw = hasBody ? bodyMatch[1] : '';
  const bodyLine = hasBody ? `  body: JSON.stringify(${bodyRaw || '{}'}),\n` : '';
  return `const url = "${url}";\n\nconst response = await fetch(url, {\n  method: "${method}",\n  headers: {\n${headerLines || '    "Content-Type": "application/json",'}\n  },\n${bodyLine}});\n\nconst data = await response.json();\nconsole.log(data);`;
};

const toPyFromCurl = (curlCode) => {
  const src = String(curlCode || '');
  const urlMatch = src.match(/"([^"]+)"/);
  const methodMatch = src.match(/-X\s+([A-Z]+)/i);
  const headerMatches = [...src.matchAll(/-H\s+"([^"]+)"/g)];
  const bodyMatch = src.match(/-d\s+'([\s\S]*)'$/m);
  const url = urlMatch ? urlMatch[1] : 'https://api.example.com/path';
  const method = methodMatch ? methodMatch[1].toUpperCase() : 'GET';
  const headers = headerMatches
    .map((h) => h[1])
    .filter(Boolean)
    .map((line) => {
      const i = line.indexOf(':');
      if (i === -1) return null;
      const k = line.slice(0, i).trim();
      const v = line.slice(i + 1).trim();
      return [k, v];
    })
    .filter(Boolean);
  const hasBody = Boolean(bodyMatch && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method));
  const headerLines = headers.map(([k, v]) => `    "${k}": "${v}",`).join('\n');
  const bodyRaw = hasBody ? bodyMatch[1] : '';
  const bodyLine = hasBody ? `payload = ${bodyRaw || '{}'}\n\n` : '';
  const requestArgs = hasBody ? ', headers=headers, json=payload' : ', headers=headers';
  return `import requests\n\nurl = "${url}"\nheaders = {\n${headerLines || '    "Content-Type": "application/json",'}\n}\n\n${bodyLine}response = requests.request("${method}", url${requestArgs})\nprint(response.json())`;
};

const Documentation = () => {
  const [lang, setLang] = useState('ru');
  const { copiedKey, copy } = useCopy();
  const [search, setSearch] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const documentationSections = useMemo(() => getDocumentationSections(lang), [lang]);
  const [activeId, setActiveId] = useState(documentationSections?.[0]?.id || 'getting-started');
  const [navOpen, setNavOpen] = useState({});
  useEffect(() => {
    if (!documentationSections?.length) return;
    if (documentationSections.some((s) => s.id === activeId)) return;
    setActiveId(documentationSections[0].id);
  }, [documentationSections, activeId]);

  useEffect(() => {
    if (!documentationSections?.length) return;
    const hashId = getHashSectionId();
    if (hashId && documentationSections.some((s) => s.id === hashId)) {
      setActiveId(hashId);
      return;
    }
    setHashSectionId(documentationSections[0].id, true);
  }, [documentationSections]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onHashChange = () => {
      const hashId = getHashSectionId();
      if (!hashId) return;
      if (!documentationSections?.some((s) => s.id === hashId)) return;
      setActiveId(hashId);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [documentationSections]);

  const normalizedQuery = String(search || '').trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!normalizedQuery) return documentationSections;
    const blockToText = (b) => {
      if (!b || typeof b !== 'object') return '';
      if (b.type === 'text') return typeof b.value === 'string' ? b.value : '';
      if (b.type === 'code') return `${b.title || ''} ${typeof b.value === 'string' ? b.value : ''}`;
      if (b.type === 'list') return `${b.title || ''} ${(b.items || []).join(' ')}`;
      return `${b.title || ''} ${typeof b.value === 'string' ? b.value : ''}`;
    };
    return documentationSections.filter((s) => {
      const hay = `${s.title || ''} ${s.category || ''} ${(s.blocks || [])
        .map((b) => blockToText(b))
        .join(' ')}`.toLowerCase();
      return hay.includes(normalizedQuery);
    });
  }, [normalizedQuery, documentationSections]);

  const grouped = useMemo(() => groupByCategory(filtered), [filtered]);
  const active = useMemo(
    () => filtered.find((s) => s.id === activeId) || filtered[0] || null,
    [activeId, filtered],
  );

  const openSection = (id) => {
    setActiveId(id);
    setMobileMenuOpen(false);
    setHashSectionId(id);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const ui = useMemo(() => {
    const isEn = lang === 'en';
    return {
      title: isEn ? 'API documentation' : 'Документация API',
      search: isEn ? 'Search documentation' : 'Поиск по документации',
      copied: isEn ? 'Copied' : 'Скопировано',
      copy: isEn ? 'Copy' : 'Копировать',
      technical: isEn ? 'Public technical documentation.' : 'Публичная техническая документация.',
      ui: isEn ? 'UI' : 'UI',
      api: isEn ? 'API' : 'API',
      authRequired: isEn ? 'requires authentication' : 'требует авторизации',
      sendRequest: isEn ? 'Copy' : 'Копировать',
      sendRequestHint: isEn ? 'Sandbox mode: copy curl only' : 'Sandbox режим: только копирование curl',
      requestExample: isEn ? 'Request example' : 'Пример запроса',
      codeExample: isEn ? 'Code example' : 'Пример кода',
      tableField: isEn ? 'Field' : 'Поле',
      tableCode: isEn ? 'Code' : 'Код',
      tableType: isEn ? 'Type' : 'Тип',
      tableRequired: isEn ? 'Required' : 'Обяз.',
      tableDescription: isEn ? 'Description' : 'Описание',
      openApi: isEn ? 'OpenAPI JSON' : 'OpenAPI JSON',
      llmDocs: isEn ? 'LLM docs' : 'LLM docs',
    };
  }, [lang]);

  const docsLinks = useMemo(() => {
    const openApiUrl =
      (process.env.REACT_APP_PUBLIC_OPENAPI_URL && String(process.env.REACT_APP_PUBLIC_OPENAPI_URL).trim()) ||
      'https://api.loyalclub.ru/docs/openapi.yaml';
    const llmUrl =
      (process.env.REACT_APP_PUBLIC_LLM_DOCS_URL && String(process.env.REACT_APP_PUBLIC_LLM_DOCS_URL).trim()) ||
      'https://api.loyalclub.ru/llms.txt';
    return { openApiUrl, llmUrl };
  }, []);

  const CodeBlockLocalized = ({ code, blockKey }) => {
    const isCurl = /^\s*curl\s+/i.test(String(code || ''));
    const [tab, setTab] = useState('curl');
    const buttonText = copiedKey === `${blockKey}:${tab}` ? ui.copied : ui.copy;
    const tabs = isCurl
      ? [
          { id: 'curl', label: 'curl', code: String(code || '') },
          { id: 'javascript', label: 'javascript', code: toJsFromCurl(code) },
          { id: 'python', label: 'python', code: toPyFromCurl(code) },
        ]
      : [{ id: 'code', label: 'code', code: String(code || '') }];
    const activeTab = tabs.find((t) => t.id === tab) || tabs[0];
    return (
      <CodeCard>
        <CodeHeader>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 6 }}>
              {tabs.map((t) => (
                <button
                  key={`${blockKey}-${t.id}`}
                  type="button"
                  onClick={() => setTab(t.id)}
                  style={{
                    border: '1px solid rgba(148,163,184,0.4)',
                    borderRadius: 8,
                    fontSize: 11,
                    padding: '3px 8px',
                    color: '#e2e8f0',
                    background: activeTab.id === t.id ? 'rgba(148,163,184,0.32)' : 'rgba(15,23,42,0.32)',
                    cursor: 'pointer',
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <CopyButton type="button" onClick={() => copy(`${blockKey}:${tab}`, activeTab.code)}>
            {buttonText}
          </CopyButton>
        </CodeHeader>
        <Code>{activeTab.code}</Code>
      </CodeCard>
    );
  };

  const isCompactListBlock = (b) => b?.type === 'list' && COMPACT_LIST_TITLES.has(String(b.title || '').trim());
  const isTabularListBlock = (b) => {
    const title = String(b?.title || '').trim();
    return b?.type === 'list' && (TABLE_LIST_TITLES.has(title) || isRequestFieldsTitle(title));
  };
  const isResponseCodesBlock = (b) =>
    String(b?.title || '').trim() === 'Коды ответов' || String(b?.title || '').trim() === 'Response codes';

  const hasAuthorizationHeader = (blocks) =>
    (blocks || []).some(
      (b) =>
        b?.type === 'list' &&
        (String(b.title || '').trim() === 'Заголовки' || String(b.title || '').trim() === 'Headers') &&
        (b.items || []).some((it) => String(it || '').toLowerCase().includes('authorization')),
    );

  const getPrimaryCurl = (blocks) =>
    (blocks || []).find((b) => b?.type === 'code' && /^\s*curl\s+/i.test(String(b.value || '')))?.value || '';

  const renderBlocks = (sectionId, blocks) =>
    (() => {
      const out = [];
      const list = blocks || [];
      let idx = 0;
      while (idx < list.length) {
        const b = list[idx];
        if (b?.type === 'text') {
          const lead = parseRequestLeadText(b.value);
          if (lead) {
            const methodColor =
              lead.method === 'GET'
                ? '#16a34a'
                : lead.method === 'POST'
                  ? '#2563eb'
                  : lead.method === 'PUT'
                    ? '#9333ea'
                    : lead.method === 'DELETE'
                      ? '#dc2626'
                      : '#0f766e';
            out.push(
              <Section key={`${sectionId}-${idx}`}>
                <div
                  style={{
                    border: '1px solid #dbeafe',
                    borderRadius: 12,
                    background: '#f8fbff',
                    padding: '10px 12px',
                    display: 'grid',
                    gap: 8,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span
                      style={{
                        background: methodColor,
                        color: '#fff',
                        borderRadius: 8,
                        padding: '4px 8px',
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: 0.2,
                      }}
                    >
                      {lead.method}
                    </span>
                    <InlineCode>{lead.path}</InlineCode>
                  </div>
                  <div style={{ fontSize: 14, lineHeight: 1.45 }}>{lead.description}</div>
                </div>
              </Section>,
            );
          } else {
            out.push(<Text key={`${sectionId}-${idx}`}>{b.value}</Text>);
          }
          idx += 1;
          continue;
        }
        if (isCompactListBlock(b)) {
          const compact = [];
          let j = idx;
          while (j < list.length && isCompactListBlock(list[j])) {
            compact.push({ block: list[j], index: j });
            j += 1;
          }
          out.push(
            <Section key={`${sectionId}-compact-${idx}`} id={`${sectionId}-${idx}`}>
              <div style={{ display: 'grid', gap: 10 }}>
                {compact.map(({ block, index }) => (
                  <div key={`${sectionId}-${index}`}>
                    <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>{block.title || 'Section'}</div>
                    {isTabularListBlock(block) ? (
                      <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: isResponseCodesBlock(block) ? '0.9fr 3.1fr' : '1.2fr 0.8fr 0.6fr 2fr',
                            gap: 0,
                            background: '#f8fafc',
                            borderBottom: '1px solid #e2e8f0',
                            fontSize: 12,
                            fontWeight: 700,
                            color: '#334155',
                          }}
                        >
                          <div style={{ padding: '8px 10px' }}>{isResponseCodesBlock(block) ? ui.tableCode : ui.tableField}</div>
                          {!isResponseCodesBlock(block) && <div style={{ padding: '8px 10px' }}>{ui.tableType}</div>}
                          {!isResponseCodesBlock(block) && <div style={{ padding: '8px 10px' }}>{ui.tableRequired}</div>}
                          <div style={{ padding: '8px 10px' }}>{ui.tableDescription}</div>
                        </div>
                        {(block.items || []).map((it, i) => {
                          const row = parseListRow(block.title, it, lang);
                          return (
                            <div
                              key={`${sectionId}-${index}-${i}`}
                              style={{
                                display: 'grid',
                                gridTemplateColumns: isResponseCodesBlock(block)
                                  ? '0.9fr 3.1fr'
                                  : '1.2fr 0.8fr 0.6fr 2fr',
                                borderBottom: i === (block.items || []).length - 1 ? 'none' : '1px solid #eef2f7',
                                fontSize: 13,
                              }}
                            >
                              <div style={{ padding: '8px 10px', fontWeight: 600 }}>{row.field}</div>
                              {!isResponseCodesBlock(block) && (
                                <div style={{ padding: '8px 10px', color: '#475569' }}>{row.type}</div>
                              )}
                              {!isResponseCodesBlock(block) && (
                                <div style={{ padding: '8px 10px', color: '#475569' }}>{row.required}</div>
                              )}
                              <div style={{ padding: '8px 10px' }}>{row.description}</div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.45, fontSize: 14 }}>
                        {(block.items || []).map((it, i) => (
                          <li key={`${sectionId}-${index}-${i}`} style={{ margin: '4px 0' }}>
                            {it}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </Section>,
          );
          idx = j;
          continue;
        }
        if (b?.type === 'list') {
          out.push(
            <Section key={`${sectionId}-${idx}`} id={`${sectionId}-${idx}`}>
              <SectionTitle>{b.title || 'Section'}</SectionTitle>
              <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.55, fontSize: 14 }}>
                {(b.items || []).map((it, i) => (
                  <li key={`${sectionId}-${idx}-${i}`} style={{ margin: '6px 0' }}>
                    {it}
                  </li>
                ))}
              </ul>
            </Section>,
          );
          idx += 1;
          continue;
        }
        if (b?.type === 'code') {
          const key = b.key || `${sectionId}-${idx}`;
          const normalizedTitle = String(b.title || '').trim().toLowerCase();
          const sectionTitle =
            normalizedTitle === 'curl'
              ? ui.requestExample
              : b.title || ui.codeExample;
          out.push(
            <Section key={`${sectionId}-${idx}`}>
              <SectionTitle>{sectionTitle}</SectionTitle>
              <CodeBlockLocalized code={b.value || ''} blockKey={key} />
            </Section>,
          );
          idx += 1;
          continue;
        }
        idx += 1;
      }
      return out;
    })();

  return (
    <Layout>
      <SidebarBackdrop
        type="button"
        aria-label="Close documentation menu"
        $open={mobileMenuOpen}
        onClick={() => setMobileMenuOpen(false)}
      />
      <Sidebar $open={mobileMenuOpen}>
        <SidebarHeader>
          <SidebarTitle>{ui.title}</SidebarTitle>
          <LangRow>
            <LangButton type="button" $active={lang !== 'en'} onClick={() => setLang('ru')}>
              RU
            </LangButton>
            <LangButton type="button" $active={lang === 'en'} onClick={() => setLang('en')}>
              EN
            </LangButton>
          </LangRow>
        </SidebarHeader>
        <SearchBox>
          <SearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={ui.search}
          />
        </SearchBox>
        {grouped.map(([category, items]) => (
          <div key={category}>
            <NavGroupTitle>{category}</NavGroupTitle>
            {(() => {
              const navGroups = groupByNav(items);
              const plain = (items || [])
                .filter((s) => !s.navGroup)
                .sort((a, b) => (a.navOrder ?? 0) - (b.navOrder ?? 0) || (a.title || '').localeCompare(b.title || ''));

              return (
                <>
                  {plain.map((s) => (
                    <NavItem
                      key={s.id}
                      type="button"
                      $active={s.id === activeId}
                      onClick={() => openSection(s.id)}
                      $indent={0}
                    >
                      {s.navLabel || s.title}
                    </NavItem>
                  ))}

                  {navGroups.map((g) => (
                    <div key={g.group}>
                      {(() => {
                        const gKey = `g::${category}::${g.group}`;
                        const gIsOpen = navOpen[gKey] ?? false;
                        return (
                          <>
                            <NavSectionTitle type="button" onClick={() => setNavOpen((p) => ({ ...p, [gKey]: !gIsOpen }))}>
                              <span>{g.group}</span>
                              <NavChevron $open={gIsOpen}>{'>'}</NavChevron>
                            </NavSectionTitle>
                            {gIsOpen && (
                              <>
                                {g.items.map((s) => (
                                  <NavItem
                                    key={s.id}
                                    type="button"
                                    $active={s.id === activeId}
                                    onClick={() => openSection(s.id)}
                                    $indent={1}
                                  >
                                    {s.navLabel || s.title}
                                  </NavItem>
                                ))}
                                {g.subgroups.map((sg) => {
                                  const sgKey = `sg::${category}::${g.group}::${sg.subgroup}`;
                                  const sgIsOpen = navOpen[sgKey] ?? false;
                                  return (
                                    <div key={sg.subgroup}>
                                      <NavSubGroupTitle
                                        type="button"
                                        onClick={() => setNavOpen((p) => ({ ...p, [sgKey]: !sgIsOpen }))}
                                      >
                                        <span>{sg.subgroup}</span>
                                        <NavChevron $open={sgIsOpen}>{'>'}</NavChevron>
                                      </NavSubGroupTitle>
                                      {sgIsOpen &&
                                        sg.items.map((s) => (
                                          <NavItem
                                            key={s.id}
                                            type="button"
                                            $active={s.id === activeId}
                                            onClick={() => openSection(s.id)}
                                            $indent={2}
                                          >
                                            {s.navLabel || s.title}
                                          </NavItem>
                                        ))}
                                    </div>
                                  );
                                })}
                              </>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  ))}
                </>
              );
            })()}
          </div>
        ))}
      </Sidebar>
      <Page>
        <MobileTopBar>
          <BurgerButton
            type="button"
            aria-label="Open documentation menu"
            onClick={() => setMobileMenuOpen((v) => !v)}
          >
            ≡
          </BurgerButton>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{ui.title}</div>
        </MobileTopBar>
        <Container>
          <Header id={active?.id || undefined}>
            <Title>{active?.title || ui.title}</Title>
            <Subtitle>
              {ui.technical} {ui.ui}: <InlineCode>https://app.loyalclub.ru</InlineCode>, {ui.api}:{' '}
              <InlineCode>https://api.loyalclub.ru</InlineCode>
            </Subtitle>
            <HeaderLinks>
              <HeaderLinkButton href={docsLinks.openApiUrl} target="_blank" rel="noopener noreferrer">
                {ui.openApi}
              </HeaderLinkButton>
              <HeaderLinkButton href={docsLinks.llmUrl} target="_blank" rel="noopener noreferrer">
                {ui.llmDocs}
              </HeaderLinkButton>
            </HeaderLinks>
          </Header>

          {(() => {
            const endpoint = parseEndpointTitle(active?.title || '');
            if (!endpoint) return null;
            const baseUrl = getBaseUrlFromBlocks(active?.blocks || []) || 'https://api.loyalclub.ru';
            const primaryCurl = getPrimaryCurl(active?.blocks || []);
            const endpointCopyKey = `endpoint-request:${active?.id || ''}`;
            const authRequired = hasAuthorizationHeader(active?.blocks || []);
            const methodColor =
              endpoint.method === 'GET'
                ? '#16a34a'
                : endpoint.method === 'POST'
                  ? '#2563eb'
                  : endpoint.method === 'PUT'
                    ? '#d97706'
                    : endpoint.method === 'DELETE'
                      ? '#dc2626'
                      : '#475569';
            return (
              <Section>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span
                    style={{
                      padding: '6px 10px',
                      borderRadius: 10,
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: 12,
                      background: methodColor,
                      letterSpacing: '0.03em',
                    }}
                  >
                    {endpoint.method}
                  </span>
                  <InlineCode>{buildEndpointUrl(baseUrl, endpoint.path)}</InlineCode>
                  {authRequired && (
                    <span
                      style={{
                        background: '#7f1d1d',
                        color: '#fff',
                        borderRadius: 8,
                        padding: '4px 8px',
                        fontSize: 11,
                        fontWeight: 600,
                      }}
                    >
                      {ui.authRequired}
                    </span>
                  )}
                  {primaryCurl && (
                    <button
                      type="button"
                      onClick={() => copy(endpointCopyKey, primaryCurl)}
                      title={ui.sendRequestHint}
                      style={{
                        border: '1px solid #cbd5e1',
                        background: '#fff',
                        color: '#1e293b',
                        borderRadius: 8,
                        padding: '6px 10px',
                        fontSize: 12,
                        cursor: 'pointer',
                        fontWeight: 600,
                      }}
                    >
                      {copiedKey === endpointCopyKey ? ui.copied : ui.sendRequest}
                    </button>
                  )}
                </div>
              </Section>
            );
          })()}

          {renderBlocks(active?.id || 'section', active?.blocks || [])}
        </Container>
      </Page>
    </Layout>
  );
};

export default Documentation;
